import { ManagementState } from 'client/store'
import { BookingCalendarSteps } from 'common/components/entities/Calendar/constants'
import { PaymentFormStepTypeEnum } from 'common/enums/PaymentFormStepTypeEnum'
import { AnyAction } from 'redux'
import { SidebarTabEnum } from 'src/shared/enums/website-enums'
import { EntityIdType } from 'src/shared/schemas/entity-id.schema'
import {
  ALLOW_CODE_DIALOG_PORTAL,
  CHANGE_BOOKING_CALENDAR_STEP_TYPE,
  CHANGE_TWO_STEP_PAYMENT_FORM_STEP_TYPE,
  CLOSE_CODE_DIALOG,
  FETCH_COLORS,
  FETCH_EXIT_URL,
  FETCH_HAS_FACEBOOK_CONVERSION_PIXEL,
  FETCH_USER,
  HAS_LOST_ENTITIES,
  HAS_MASTER_BLOCK_IDS_WITHOUT_MASTER_ROOT,
  HAS_MASTER_BLOCK_ROOT_WITHOUT_MASTER_BLOCK_ID,
  SET_ACTIVE_SIDEBAR_TAB,
  SHOW_SURVEY_RESULT,
  SUCCESS,
  TOGGLE_ACTIVE_POPUP,
  TOGGLE_CODE_DIALOG,
  TOGGLE_CREATE_BLOCK_DIALOG,
  TOGGLE_DROP_DOWN_MENU_OPENED,
  TOGGLE_INSTANT_UPLOAD,
  TOGGLE_NEED_SCROLL,
  TOGGLE_SETTINGS,
  TOGGLE_SETTINGS_OVERLAY,
  TOGGLE_SIDE_MENU_OPENED,
} from '../actionTypes'
import {
  NEED_SCROLL,
  SET_EDITING_ENTITY,
  SET_SETTINGS_EDITING,
  SET_SHOWN_SEARCH_RESULTS,
  SET_USER_COLORS,
  SWITCH_ENTITY_INNER_ITEM_ACTIVE_INDEX,
  TOGGLE_PAYPAL_SETTINGS,
} from '../store/management/managementActionTypes'

const defaultReducerState: ManagementState = {
  activeSidebarTab: null,

  isCodeDialogOpened: false,
  isCodeDialogPortalAllowed: false,
  activePopupId: null,
  settingsEditing: {
    id: null,
    needOverlay: true,
  },
  editingEntityId: null,
  needScroll: false,
  createBlockDialogEntityId: null,
  isSettingsOverlayVisible: false,
  isPaypalSettingsOpened: false,
  exitUrl: null,
  user: {},
  twoStepPaymentFormStepType: PaymentFormStepTypeEnum.STEP_OPT_IN,
  bookingCalendarStepType: BookingCalendarSteps.CalendarStep,
  hasLostEntities: false,
  hasMasterBlockRootWithoutMasterBlockId: false,
  hasMasterBlockIdsWithoutMasterRoot: false,
  sideMenuOpened: false,
  dropDownMenuOpened: false,
  instantUploadEnabled: false,
  isSurveyResult: false,
  entityInnerItemActiveIndex: {},
  customColors: [],
  shownSearchResults: [],
}

const managementReducer = (
  // biome-ignore lint/style/useDefaultParameterLast: impossible to fix for now
  state = defaultReducerState,
  action: AnyAction,
): ManagementState => {
  const { type, payload } = action

  switch (type) {
    case SET_EDITING_ENTITY:
      return {
        ...state,
        settingsEditing: { id: payload, needOverlay: false },
        editingEntityId: payload,
      }
    case SET_SETTINGS_EDITING:
      return {
        ...state,
        settingsEditing: {
          ...payload,
        },
        editingEntityId: payload.id,
      }
    case CHANGE_TWO_STEP_PAYMENT_FORM_STEP_TYPE:
      return {
        ...state,
        twoStepPaymentFormStepType: payload,
      }
    case CHANGE_BOOKING_CALENDAR_STEP_TYPE:
      return {
        ...state,
        bookingCalendarStepType: payload,
      }
    case SWITCH_ENTITY_INNER_ITEM_ACTIVE_INDEX:
      return {
        ...state,
        entityInnerItemActiveIndex: {
          ...state.entityInnerItemActiveIndex,
          [payload.entityId]: payload.entityInnerItemActiveIndex,
        },
      }
    case TOGGLE_CODE_DIALOG:
      return {
        ...state,
        isCodeDialogOpened: payload,
      }
    case CLOSE_CODE_DIALOG:
      return {
        ...state,
        isCodeDialogOpened: false,
        isCodeDialogPortalAllowed: false,
      }
    case ALLOW_CODE_DIALOG_PORTAL:
      return {
        ...state,
        isCodeDialogPortalAllowed: payload,
      }
    case TOGGLE_ACTIVE_POPUP:
      return {
        ...state,
        activePopupId: payload.id,
      }
    case TOGGLE_SETTINGS:
      return {
        ...state,
        settingsEditing: {
          id: payload.id,
          needOverlay: false,
        },
        editingEntityId: payload.id,
      }
    case TOGGLE_INSTANT_UPLOAD:
      return {
        ...state,
        instantUploadEnabled: payload,
      }
    case TOGGLE_SIDE_MENU_OPENED:
      return {
        ...state,
        sideMenuOpened: !state.sideMenuOpened,
      }
    case TOGGLE_DROP_DOWN_MENU_OPENED:
      return {
        ...state,
        dropDownMenuOpened: !state.dropDownMenuOpened,
      }
    case TOGGLE_SETTINGS_OVERLAY:
      return {
        ...state,
        isSettingsOverlayVisible: payload,
      }
    case TOGGLE_NEED_SCROLL:
    case NEED_SCROLL:
      return {
        ...state,
        needScroll: payload,
      }
    case TOGGLE_CREATE_BLOCK_DIALOG:
      return {
        ...state,
        createBlockDialogEntityId: payload,
      }
    case TOGGLE_PAYPAL_SETTINGS:
      return {
        ...state,
        isPaypalSettingsOpened: payload,
      }
    case FETCH_EXIT_URL:
      return {
        ...state,
        exitUrl: payload,
      }
    case FETCH_USER + SUCCESS:
      return {
        ...state,
        user: payload,
      }
    case FETCH_COLORS + SUCCESS:
      return {
        ...state,
        customColors: payload,
      }
    case FETCH_HAS_FACEBOOK_CONVERSION_PIXEL + SUCCESS:
      return {
        ...state,
        hasFacebookConversionPixel: payload,
      }
    case HAS_LOST_ENTITIES:
      return {
        ...state,
        hasLostEntities: payload,
      }
    case HAS_MASTER_BLOCK_ROOT_WITHOUT_MASTER_BLOCK_ID:
      return {
        ...state,
        hasMasterBlockRootWithoutMasterBlockId: payload,
      }
    case HAS_MASTER_BLOCK_IDS_WITHOUT_MASTER_ROOT:
      return { ...state, hasMasterBlockIdsWithoutMasterRoot: payload }
    case SHOW_SURVEY_RESULT:
      return { ...state, isSurveyResult: payload }
    case SET_USER_COLORS:
      return { ...state, customColors: payload }
    case SET_ACTIVE_SIDEBAR_TAB:
      return { ...state, activeSidebarTab: payload }
    case SET_SHOWN_SEARCH_RESULTS:
      return { ...state, shownSearchResults: payload }
    default:
      return state
  }
}

export default managementReducer

export const isPopupManagement = ({ activeSidebarTab }: ManagementState) =>
  activeSidebarTab === SidebarTabEnum.PopupManagement

export const isPagesManagementActive = ({
  activeSidebarTab,
}: ManagementState) => activeSidebarTab === SidebarTabEnum.PagesManagement

export const isWebsiteStylesSettings = ({
  activeSidebarTab,
}: ManagementState) => activeSidebarTab === SidebarTabEnum.WebsiteStylesSettings

export const isPageEditing = ({ activeSidebarTab }: ManagementState) =>
  activeSidebarTab === SidebarTabEnum.PageEditing

export const isWebsiteSettings = ({ activeSidebarTab }: ManagementState) =>
  activeSidebarTab === SidebarTabEnum.WebsiteSettings

export const getSettingsEditing = ({ settingsEditing }: ManagementState) =>
  settingsEditing

export const isEditing = (
  { settingsEditing }: ManagementState,
  id: EntityIdType,
) => settingsEditing.id === id

export const getActiveSidebarTab = ({ activeSidebarTab }: ManagementState) =>
  activeSidebarTab

export const getIsEditing = (
  { settingsEditing }: ManagementState,
  id: EntityIdType,
) => settingsEditing.id === id

export const getEditingId = ({ settingsEditing }: ManagementState) =>
  settingsEditing.id

export const isSomethingEditing = ({
  settingsEditing: { id },
}: ManagementState) => id !== null

export const isPopupActive = ({ activePopupId }: ManagementState) =>
  activePopupId !== null

export const getActivePopupId = ({ activePopupId }: ManagementState) =>
  activePopupId

export const isPopupEditing = ({ activePopupId }: ManagementState) =>
  activePopupId !== null

export const isCreateBlockDialogActive = ({
  createBlockDialogEntityId,
}: ManagementState) => Boolean(createBlockDialogEntityId)

export const isBlockSaveDialogOpened = (
  { createBlockDialogEntityId }: ManagementState,
  entityId: EntityIdType,
) => createBlockDialogEntityId === entityId

export const getCreateBlockDialogEntityId = ({
  createBlockDialogEntityId,
}: ManagementState) => createBlockDialogEntityId

export const getNeedScroll = ({ needScroll }: ManagementState) => needScroll

export const getExitUrl = ({ exitUrl }: ManagementState) => exitUrl

export const isUserAdmin = ({ user }: ManagementState) => user?.isAdmin

export const isSettingsOverlayVisible = ({
  isSettingsOverlayVisible,
}: ManagementState) => isSettingsOverlayVisible

export const getTwoStepPaymentFormStepType = ({
  twoStepPaymentFormStepType,
}: ManagementState) => twoStepPaymentFormStepType

export const getBookingCalendarStepType = ({
  bookingCalendarStepType,
}: ManagementState) => bookingCalendarStepType

export const hasLostEntities = ({ hasLostEntities }: ManagementState) =>
  hasLostEntities

export const hasMasterBlockRootWithoutMasterBlockId = ({
  hasMasterBlockRootWithoutMasterBlockId,
}: ManagementState) => hasMasterBlockRootWithoutMasterBlockId

export const hasMasterBlockIdsWithoutMasterRoot = ({
  hasMasterBlockIdsWithoutMasterRoot,
}: ManagementState) => hasMasterBlockIdsWithoutMasterRoot

export const isSideMenuOpened = (state: ManagementState) => state.sideMenuOpened

export const isPaypalSettingsOpened = (state: ManagementState) =>
  state.isPaypalSettingsOpened

export const isDropDownMenuOpened = (state: ManagementState) =>
  state.dropDownMenuOpened

export const isCodeDialogOpened = (state: ManagementState) =>
  state.isCodeDialogOpened

export const isCodeDialogPortalAllowed = (state: ManagementState) =>
  state.isCodeDialogPortalAllowed

export const isInstantUploadEnabled = (state: ManagementState) =>
  state.instantUploadEnabled

export const isSurveyResult = (state: ManagementState) => state.isSurveyResult

export const getEntityInnerItemActiveIndex = (
  state: ManagementState,
  entityId: EntityIdType,
) => state.entityInnerItemActiveIndex?.[entityId] || 0

export const getCustomColors = ({ customColors }: ManagementState) =>
  customColors

export const getShownSearchResults = ({
  shownSearchResults,
}: ManagementState) => ({ list: shownSearchResults })

export const selectors = {
  getEntityInnerItemActiveIndex,
  isPopupManagement,
  isPagesManagementActive,
  isWebsiteStylesSettings,
  isEditing,
  isPageEditing,
  isWebsiteSettings,
  isPopupActive,
  getEditingId,
  getActivePopupId,
  getActiveSidebarTab,
  getExitUrl,
  isSomethingEditing,
  isUserAdmin,
  getTwoStepPaymentFormStepType,
  getBookingCalendarStepType,
  hasLostEntities,
  hasMasterBlockRootWithoutMasterBlockId,
  hasMasterBlockIdsWithoutMasterRoot,
  isCodeDialogOpened,
  isCodeDialogPortalAllowed,
  isSideMenuOpened,
  isDropDownMenuOpened,
  isInstantUploadEnabled,
  isSurveyResult,
  getCustomColors,
  getShownSearchResults,
}
